import { Divider, Grid, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import globalStyles from "../../../assets/styles/global.module.scss";
import style from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import webhookStyle from "../../../assets/styles/componentStyles/webhookStyles/webhookStyle.module.scss";
import feedbackStyles from "../../../assets/styles/componentStyles/feedbackDialog.module.scss"
import chatStyles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import Required from '../../utility/required';
import GlobalButton from '../../utility/globalButton/button';
import { payloadCreateUpdateWebhook, WebhookExternalConfig } from '../../../models/axiosModel';
import { getCreateUpdateWebhookPayload } from './webhookConstants';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import agent from '../../../services/api';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';

interface CreateUpdateWebhookPopupProps {
    selectedWebhook: WebhookExternalConfig | null,
    setFlagManageWebhookPopup: React.Dispatch<React.SetStateAction<boolean>>, 
    getWebhookExternalDataApiCall: () => Promise<void>,
    getWebhookOrganizationListApi:  () => Promise<void>,
}

const CreateUpdateWebhookPopup: React.FC<CreateUpdateWebhookPopupProps> = (props) => {

    const { selectedWebhook, setFlagManageWebhookPopup, getWebhookExternalDataApiCall, getWebhookOrganizationListApi } = props;
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const [payload, setPayload] = useState<payloadCreateUpdateWebhook>(getCreateUpdateWebhookPayload(selectedWebhook));
    const [flagValidJson, setFlagValidJson] = useState<boolean>(true);

    const innerHeight = window.innerHeight - 210;
    
    const disableSaveWebhook = () => {
        if(payload.webhook_external_company_name.trim().length > 0 && payload?.webhook_external_send_response_code && payload.webhook_external_send_response_value?.trim()?.length > 0 && payload.sample_webhook_response_json?.trim()?.length > 0 && flagValidJson){
            return false;
        } 
        return true;
    } 

    const saveWebhook = async () => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true}));
        const res = await agent.createUpdateWebhookExternal.post(payload); 
        if(!(res.status)){
            handleError(res);
        } else {
            showSuccess(`Webhook ${selectedWebhook ? 'updated' : 'created'} successfully!`)
            await getWebhookExternalDataApiCall(); 
            if(selectedWebhook){
                await getWebhookOrganizationListApi();
            }
        }
        setFlagManageWebhookPopup(false);
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false}));
    } 

    const onChangeOfField = (type: string, value: any) => { 
        if(type == "sampleJson"){ // Json Validation for sample response json field.
            let replaceStr = value.replace(/\n(?=\s*[{[(])/g, '').replace(/(?<=[}\])])\n/g, ''); 
            try {
                JSON.parse(replaceStr); 
                setFlagValidJson(true);
            } catch (e){
                setFlagValidJson(value?.length > 0 ? false : true);
            }
        }
        setPayload((prev) => {
            switch(type){
                case 'company': 
                    return {
                        ...prev,
                        webhook_external_company_name: value, 
                    } 
                case 'description': 
                    return {
                        ...prev,
                        webhook_external_description: value,  
                    }
                case 'responseCode': 
                    return {
                        ...prev, 
                        webhook_external_send_response_code: value, 
                    } 
                case 'responseValue': 
                    return {
                        ...prev,
                        webhook_external_send_response_value: value,
                    }  
                case 'sampleJson': 
                    return {
                        ...prev, 
                        sample_webhook_response_json: value,
                    }
                default: 
                    return prev;
            }
        }); 
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                        <Grid item className={globalStyles.textCenter}>
                            <Typography className={style.popupTitleTypography}>{`${selectedWebhook ? 'Edit' : 'Create'} Webhook`}</Typography>
                        </Grid>
                        
                        <Divider className={style.popupTitleDivider} />
                        
                        <Grid item>
                            <Grid container className={style.popupBodyGridContainer} gap={2} sx={{overflow: "auto", maxHeight: `${innerHeight}px !important` }}>
                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.popupFieldTypography}>Company Name <Required /></Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                value={payload.webhook_external_company_name}
                                                disabled={selectedWebhook ? true : false}
                                                onChange={(e) => onChangeOfField("company", e.target.value)}
                                                placeholder='Enter Company Name'
                                                className={`${style.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.popupFieldTypography}>Webhook Description</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <textarea
                                                rows={4}
                                                placeholder="Enter Webhook Description"
                                                className={`${feedbackStyles.longTextFeedbackInputField} ${chatStyles.cannedResponseMessageTextarea} ${webhookStyle.sampleResponseJsonTextArea}`}
                                                value={payload.webhook_external_description}
                                                onChange={(e) => onChangeOfField("description", e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>  

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.popupFieldTypography}>Webhook Sample Response Json <Required /></Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <textarea
                                                rows={4}
                                                placeholder="Enter Webhook Sample Response Json"
                                                className={`${feedbackStyles.longTextFeedbackInputField} ${chatStyles.cannedResponseMessageTextarea} ${webhookStyle.sampleResponseJsonTextArea}`}
                                                value={payload.sample_webhook_response_json}
                                                onChange={(e) => onChangeOfField("sampleJson", e.target.value)}
                                            />
                                            {!flagValidJson && 
                                                <Typography className={`${webhookStyle.validJsonTypography}`}>Please Enter Valid JSON</Typography>
                                            } 
                                        </Grid>
                                    </Grid>
                                </Grid> 

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.popupFieldTypography}>Response Code <Required /></Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                type='number'
                                                value={payload.webhook_external_send_response_code || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if(Number(value) >= 0 || value == ""){
                                                        onChangeOfField("responseCode", Number(value));
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e") {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                placeholder='Enter Response code'
                                                className={`${style.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.popupFieldTypography}>Response Value <Required /></Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                value={payload.webhook_external_send_response_value}
                                                onChange={(e) => onChangeOfField("responseValue", e.target.value)}
                                                placeholder='Enter Response value'
                                                className={`${style.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} sx={{ padding: "20px 0" }}>
                                <Grid item>
                                    <GlobalButton
                                        buttonText="Cancel"
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='borderedButtonStyle'
                                        onClick={() => {
                                            setFlagManageWebhookPopup(false);
                                        }}
                                    >
                                    </GlobalButton>
                                </Grid>
                                <Grid item>
                                    <GlobalButton
                                        buttonText={selectedWebhook ? "Save" : "Create"}
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='primaryButtonStyle'
                                        disabled={disableSaveWebhook()}
                                        onClick={() => saveWebhook()}
                                    >
                                    </GlobalButton>
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default CreateUpdateWebhookPopup
